import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AngularMaterialModule } from '../../../../../angular-material.module';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { TemplateComponent } from '../../../../../common/template/template.component';
import { EditRequestComponent } from '../../edit-request/edit-request.component';
import { CommentComponent } from '../../comment/comment.component';
import { AdddPhotoComponent } from '../../addd-photo/addd-photo.component';
import { PreviewDialogComponent } from '../../../../../Modules/Document-master/DOCUMENT_SETUP/preview-dialog/preview-dialog.component';
import { environment } from '../../../../../../environments/environment';
import { BreadcrumbComponent } from '../../../../../common/breadcrumb/breadcrumb/breadcrumb.component';
import {
  CustomTableViewComponent,
  TableColumn,
} from '../../../../../common/custom-table-view/custom-table-view.component';
import { RouterModule } from '@angular/router';
import { RaiseRequestService } from '../../../../../services/raise-request.service';
import { snackBarService } from '../../../../../services/snack-bar.service';
import { ApiService } from '../../../../../services/api.service';
import { getAccountGroupIdFromSession } from '../../../../../Modules/assets/asset-movement/asset-movement.utils';
import { UpdateSatatusComponent } from '../../update-satatus/update-satatus.component';
import { RequestAssignComponent } from '../../raise-requests/all-requests/request-assign/request-assign.component';
import {
  EMPTY,
  Observable,
  Subject,
} from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { exportRequestsToPdf } from './all-requests-pdf-export.util';
import { RaiseRequestsComponent } from '../raise-requests.component';
import * as XLSX from 'xlsx';

declare const bootstrap: any;

// Enhanced type definitions
interface Issue {
  issue_id: number;
  issue_photo?: string;
  issuePhotos?: string[];
  status_name?: string;
  status?: number | string;
  account_id?: number | string;
  account_name?: string;
  priority_id?: number | string;
  priority_name?: string;
  issue_category_id?: number | string;
  category_name?: string;
  sub_category_name?: string;
  block?: string;
  floor_unit?: string;
  reported_name?: string;
  created_at?: string | Date;
  description?: string;
  assigned_to_name?: string;
  assigned_to?: number | string;
  created_by?: number | string;
  latest_comment?: string;
  commented_by?: string;
  commented_at?: string | Date;
  checklist_location?: string;
  approved?: string;
  approved_by_name?: string;
  is_approved?: number;
}

interface StatusOption {
  status_id: number | string;
  status: string;
}

interface CategoryOption {
  issue_category_id: number | string;
  issue_category: string;
}

interface SubCategoryOption {
  issue_category_id: number | string;
  issue_category: string;
}

interface WingOption {
  block_id: number | string;
  block_name: string;
}

interface FloorOption {
  floor_id: number | string;
  floor_name: string;
}

interface UnitTypeOption {
  unit_type: string;
}

interface UnitOption {
  floor_unit_id: number | string;
  floor_unit: string;
  block_id?: number | string;
  unit_type?: string;
}

interface AccountOption {
  account_id: number | string;
  project_name: string;
}

interface PriorityOption {
  priority_id?: number | string;
  id?: number | string;
  priority?: string;
  name?: string;
}

interface UserProfile {
  user_id: number | string;
  role: string;
  role_id?: number | string;
  first_name?: string;
  last_name?: string;
  [key: string]: unknown;
}

interface FilterCriteria {
  account: string;
  category: string;
  subCategory: string;
  status: string;
  priority: string;
  wing: string;
  floor: string;
  unitType: string;
  unit: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-all-requests',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    CommonModule,
    TemplateComponent,
    BreadcrumbComponent,
    RouterModule,
    CustomTableViewComponent,
  ],
  templateUrl: './all-requests.component.html',
  styleUrls: ['./all-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllRequestsComponent implements OnInit, OnDestroy {
  readonly tableColumns: TableColumn[] = [
    { key: 'slno', title: 'Sl No.', sortable: false, width: 70, filterable: false },
    { key: 'category_name', title: 'Category', sortable: true },
    { key: 'sub_category_name', title: 'Sub Category', sortable: true },
    {
      key: 'created_at',
      title: 'Date',
      sortable: true,
      format: (value) =>
        value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
    },
    { key: 'block', title: 'Wing', sortable: true },
    { key: 'floor_unit', title: 'Unit', sortable: true },
    { key: 'priority_name', title: 'Priority', sortable: true },
    { key: 'status_name', title: 'Status', sortable: true },
    { key: 'account_name', title: 'Project', sortable: true },
    { key: 'reported_name', title: 'Raised By', sortable: true },
    { key: 'assigned_to_name', title: 'Assigned To', sortable: true },
    { key: 'description', title: 'Description', sortable: false, width: 220 },
  ];

  // Inject dependencies
  private readonly raiseRequestService = inject(RaiseRequestService);
  private readonly snackBarService = inject(snackBarService);
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  // Constants
  readonly baseUrl = environment.baseUrl;
  readonly storageUrl = environment.storageUrl;
  private readonly CLOSED_STATUS_ID = 3;

  // Session storage values (read once)
  private readonly accountID = sessionStorage.getItem('account_id') || '';
  private readonly userID = sessionStorage.getItem('session_id') || '';
  private readonly roleID = sessionStorage.getItem('role_id') || '';

  // Reactive state using Signals
  readonly loading = signal<boolean>(false);
  readonly exportingPdf = signal<boolean>(false);
  readonly activeTab = signal<'all' | 'open' | 'closed'>('all');
  readonly viewMode = signal<'card' | 'list'>('card');
  readonly cardItemsPerPage = signal<number>(50);
  readonly cardCurrentPage = signal<number>(1);
  readonly totalCount = signal<number>(0);
  readonly filteredCount = signal<number>(0);

  readonly displayFilteredCount = computed(() => {
    const fCount = this.filteredCount();
    if (fCount !== null && fCount !== undefined && !Number.isNaN(fCount) && fCount > 0) {
      return fCount;
    }
    return this.currentRequests().length;
  });

  // Reactive filter form
  filterForm!: FormGroup;

  // Data signals
  readonly allRequestList = signal<Issue[]>([]);
  readonly categoryList = signal<CategoryOption[]>([]);
  readonly subCategoryList = signal<SubCategoryOption[]>([]);
  readonly showSubCategoryFilter = signal(false);
  readonly wingList = signal<WingOption[]>([]);
  readonly floorList = signal<FloorOption[]>([]);
  readonly unitTypeList = signal<UnitTypeOption[]>([]);
  readonly unitList = signal<UnitOption[]>([]);
  readonly statusList = signal<StatusOption[]>([]);
  readonly accountList = signal<AccountOption[]>([]);
  readonly priorityList = signal<PriorityOption[]>([]);
  readonly userProfile = signal<UserProfile | null>(null);

  // Filter signals
  readonly selectedCategory = signal<string>('');
  readonly selectedSubCategory = signal<string>('');
  readonly selectedStatus = signal<string>('');
  readonly selectedAccount = signal<string>(this.accountID);
  readonly selectedPriority = signal<string>('');
  readonly selectedWing = signal<string>('');
  readonly selectedFloor = signal<string>('');
  readonly selectedUnitType = signal<string>('');
  readonly selectedUnit = signal<string>('');
  readonly selectedStartDate = signal<string>('');
  readonly selectedEndDate = signal<string>('');

  // Carousel management
  private carouselInstances: Map<HTMLElement, any> = new Map();
  private readonly destroy$ = new Subject<void>();
  private readonly carouselInitTimeout = signal<number | null>(null);

  // Role checks based on reference table: Superadmin (1), Builder Admin (12), Staff (3, etc.)
  readonly userRoleId = computed(() => {
    const profile = this.userProfile();
    const idFromProfile = profile?.role_id != null ? Number(profile.role_id) : NaN;
    if (!Number.isNaN(idFromProfile)) {
      return idFromProfile;
    }
    const idFromSession = parseInt(this.roleID || '0', 10);
    return Number.isNaN(idFromSession) ? 0 : idFromSession;
  });

  readonly userRoleName = computed(() => {
    return String(this.userProfile()?.role || '').trim();
  });

  readonly isSuperAdmin = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    return roleId === 1 || roleName === 'Superadmin';
  });

  readonly isBuilderAdmin = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    return roleId === 12 || roleName === 'Builder Admin';
  });

  readonly isStaffUser = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    const staffRoleIds = [3, 13, 14, 15, 16, 17, 18, 19];
    const staffRoleNames = [
      'Staff', 'Builder Staff', 'Security Guard', 'Builder Manager',
      'Asset Manager', 'Checklist Inspector', 'Engineer', 'Project Manager', 'PM/APM/SE'
    ];
    return (
      staffRoleIds.includes(roleId) ||
      staffRoleNames.includes(roleName) ||
      (!this.isSuperAdmin() && !this.isBuilderAdmin())
    );
  });

  // Getter for template backwards compatibility
  get isBuilder(): boolean {
    return this.isBuilderAdmin();
  }

  // Edit Request access: Superadmin (role_id 1) and Builder Admin (role_id 12)
  canEditRequest(_issue?: Issue): boolean {
    return this.isSuperAdmin() || this.isBuilderAdmin();
  }

  private isResolvedOrDeferred(issue?: Issue): boolean {
    if (!issue) {
      return false;
    }
    const statusNum = Number(issue.status);
    const statusName = String(issue.status_name || '').toLowerCase().trim();
    return (
      statusNum === 3 ||
      statusName === 'resolved' ||
      statusName === 'deferred' ||
      statusName === 'deffered' ||
      statusName.includes('resolve') ||
      statusName.includes('defer')
    );
  }

  // Edit Status access: Superadmin, Builder Admin can update always; Staff can update unless status is Resolved or Deferred
  canUpdateStatus(issue?: Issue): boolean {
    if (this.isSuperAdmin() || this.isBuilderAdmin()) {
      return true;
    }
    if (this.isResolvedOrDeferred(issue)) {
      return false;
    }
    if (this.isStaffUser()) {
      return true;
    }
    if (!issue) {
      return true;
    }
    const currentUserId = this.userProfile()?.user_id || parseInt(this.userID || '0', 10);
    return (
      String(issue.assigned_to) === String(currentUserId) ||
      String(issue.created_by) === String(currentUserId)
    );
  }

  // Computed: All requests — returns everything from the API response
  readonly allRequests = computed(() => {
    return this.allRequestList();
  });

  // Computed: Open requests — status_id 1 (Open) or 2 (In Progress)
  readonly openRequests = computed(() => {
    const criteria = this.filterCriteria();
    if (criteria.status) {
      // If user has manually filtered by status, respect that selection
      return this.allRequestList().filter((issue) =>
        String(issue.status) === String(criteria.status)
      );
    }
    return this.allRequestList().filter((issue) => {
      const s = Number(issue.status);
      return s === 1 || s === 2;
    });
  });

  // Computed: Closed requests (status 3 only)
  readonly closedRequests = computed(() => {
    const criteria = this.filterCriteria();
    if (criteria.status) {
      return this.allRequestList();
    }
    return this.allRequestList().filter((issue) =>
      this.isClosedRequest(issue)
    );
  });

  // Computed: Filter criteria object
  readonly filterCriteria = computed<FilterCriteria>(() => ({
    account: this.selectedAccount(),
    category: this.selectedCategory(),
    subCategory: this.selectedSubCategory(),
    status: this.selectedStatus(),
    priority: this.selectedPriority(),
    wing: this.selectedWing(),
    floor: this.selectedFloor(),
    unitType: this.selectedUnitType(),
    unit: this.selectedUnit(),
    startDate: this.selectedStartDate(),
    endDate: this.selectedEndDate(),
  }));

  // Computed: Filtered all requests
  readonly filteredRequests = computed(() => {
    const requests = this.allRequests();
    const criteria = this.filterCriteria();
    return this.applyFilters(requests, criteria, true);
  });

  // Computed: Filtered open requests (status 1 & 2)
  readonly filteredOpenRequests = computed(() => {
    const requests = this.openRequests();
    const criteria = this.filterCriteria();
    return this.applyFilters(requests, criteria, true);
  });

  // Computed: Filtered closed requests
  readonly filteredClosedRequests = computed(() => {
    const requests = this.closedRequests();
    const criteria = this.filterCriteria();
    return this.applyFilters(requests, criteria, true);
  });

  // Computed: Current visible requests based on tab
  readonly currentRequests = computed(() => {
    return this.allRequestList();
  });

  // Computed: Request count for display (filtered count out of total count)
  readonly requestCount = computed(() => {
    const filtered = this.displayFilteredCount();
    const total = this.totalCount();
    if (total > 0) {
      return `${filtered} of ${total}`;
    }
    return `${filtered}`;
  });

  readonly tableData = computed(() => {
    const offset = (this.cardCurrentPage() - 1) * this.cardItemsPerPage();
    return this.currentRequests().map((issue, index) => ({
      ...issue,
      slno: offset + index + 1,
    }));
  });

  // Computed values for card view pagination (limit/offset)
  readonly paginatedCards = computed(() => {
    return this.currentRequests();
  });

  readonly cardPageRangeStart = computed(() => {
    const total = this.displayFilteredCount();
    if (total === 0) {
      return 0;
    }
    return (this.cardCurrentPage() - 1) * this.cardItemsPerPage() + 1;
  });

  readonly cardPageRangeEnd = computed(() => {
    const total = this.displayFilteredCount();
    return Math.min(
      this.cardCurrentPage() * this.cardItemsPerPage(),
      total
    );
  });

  readonly cardHasPreviousPage = computed(() => this.cardCurrentPage() > 1);

  readonly cardHasNextPage = computed(() => {
    const total = this.displayFilteredCount();
    if (total > 0) {
      return this.cardCurrentPage() * this.cardItemsPerPage() < total;
    }
    return this.allRequestList().length >= this.cardItemsPerPage();
  });

  // Client-side filter: only re-filter by status and priority since all other filters
  // (account, category, sub-category, wing, floor, unit) are already applied server-side
  // by fetchAllIssues. Applying them again on the client caused mismatches because the
  // response contains sub-category IDs in issue_category_id, not the parent category ID.
  private applyFilters(
    requests: Issue[],
    criteria: FilterCriteria,
    includePriority: boolean
  ): Issue[] {
    return requests.filter((issue) => {
      const statusMatch = criteria.status
        ? String(issue.status) === String(criteria.status)
        : true;
      const priorityMatch = includePriority
        ? criteria.priority
          ? String(issue.priority_id) === String(criteria.priority)
          : true
        : true;

      return statusMatch && priorityMatch;
    });
  }

  constructor(private readonly fb: FormBuilder) {
    // Effect to fetch accounts when user profile loads and conditions are met
    effect(() => {
      const profile = this.userProfile();
      if (profile && this.accountList().length === 0) {
        this.fetchAccounts();
      }
    });
  }

  ngOnInit(): void {
    // Initialize reactive filter form
    this.filterForm = this.fb.group({
      account: [this.selectedAccount()],
      status: [this.selectedStatus()],
      start_date: [this.selectedStartDate()],
      end_date: [this.selectedEndDate()],
      category: [this.selectedCategory()],
      sub_category: [this.selectedSubCategory()],
      priority: [this.selectedPriority()],
      wing: [this.selectedWing()],
      floor: [this.selectedFloor()],
      unit_type: [this.selectedUnitType()],
      unit: [this.selectedUnit()],
    });

    this.setupFilterFormListeners();

    // Parallel initialization with proper error handling
    this.fetchAllIssues();
    this.fetchUserProfile();
    this.allCategoryDropdown();
    this.fetchAllStatus();
    this.fetchAllPriority();
    this.loadLocationFilterDropdowns(this.selectedAccount());

    this.fetchAccounts();
  }

  /** 🔹 Raise Request Dialog */
  raiseRequests(): void {
    this.dialog
      .open(RaiseRequestsComponent, {
        autoFocus: false,
        width: '760px',
        maxWidth: '95vw',
        panelClass: 'submit-ticket-dialog',
      })
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) {
          this.fetchAllIssues();
        }
      });
  }

  private setupFilterFormListeners(): void {
    this.filterForm.get('account')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountId) => {
        this.resetDependentFilters(['wing', 'floor', 'unit_type', 'unit']);
        this.floorList.set([]);
        this.unitList.set([]);
        if (accountId) {
          this.loadLocationFilterDropdowns(accountId);
        } else {
          this.wingList.set([]);
          this.floorList.set([]);
          this.unitTypeList.set([]);
        }
      });

    this.filterForm.get('category')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((categoryId) => {
        this.resetDependentFilters(['sub_category']);
        if (categoryId) {
          this.loadSubCategoryDropdown(categoryId);
        } else {
          this.subCategoryList.set([]);
          this.showSubCategoryFilter.set(false);
        }
      });

    this.filterForm.get('wing')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['floor', 'unit']);
        this.refreshFloorDropdown();
        this.refreshUnitDropdown();
      });

    this.filterForm.get('floor')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['unit']);
        this.refreshUnitDropdown();
      });

    this.filterForm.get('unit_type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['unit']);
        this.refreshUnitDropdown();
      });
  }

  private resetDependentFilters(fields: string[]): void {
    const patch: Record<string, string> = {};
    fields.forEach((field) => {
      patch[field] = '';
    });
    this.filterForm.patchValue(patch, { emitEvent: false });
  }

  private getActiveAccountId(): string {
    return this.filterForm?.get('account')?.value
      || this.selectedAccount()
      || this.accountID
      || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupCarousels();
    const timeoutId = this.carouselInitTimeout();
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }

  // Tab management
  onTabChange(tab: 'all' | 'open' | 'closed'): void {
    this.activeTab.set(tab);
    this.cardCurrentPage.set(1);
    if (tab !== 'all' && this.filterForm) {
      this.filterForm.get('status')?.setValue('', { emitEvent: false });
      this.selectedStatus.set('');
    }
    this.fetchAllIssues();
  }

  onViewToggle(isListView: boolean): void {
    this.viewMode.set(isListView ? 'list' : 'card');
  }

  cardPreviousPage(): void {
    if (this.cardHasPreviousPage()) {
      this.cardCurrentPage.update((p) => p - 1);
      this.fetchAllIssues();
    }
  }

  cardNextPage(): void {
    if (this.cardHasNextPage()) {
      this.cardCurrentPage.update((p) => p + 1);
      this.fetchAllIssues();
    }
  }

  onCardItemsPerPageChange(size: number): void {
    this.cardItemsPerPage.set(size);
    this.cardCurrentPage.set(1);
    this.fetchAllIssues();
  }

  onTablePageChange(event: { pageIndex: number; pageSize: number; offset: number }): void {
    this.cardItemsPerPage.set(event.pageSize);
    this.cardCurrentPage.set(event.pageIndex + 1);
    this.fetchAllIssues();
  }

  async exportToPdf(): Promise<void> {
    const requests = this.currentRequests();
    if (!requests.length) {
      this.snackBarService.showError('No requests to export.');
      return;
    }

    this.exportingPdf.set(true);
    try {
      const profile = this.userProfile();
      const preparedBy = profile
        ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
        : sessionStorage.getItem('username') || 'User';

      const sanitizeSegment = (name: string): string => {
        return name
          .trim()
          .replace(/[^a-zA-Z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_+|_+$/g, '');
      };

      // Project names
      const accountMap = new Map<string, string>();
      (this.accountList() || []).forEach((acc) => {
        if (acc.account_id && acc.project_name) {
          accountMap.set(String(acc.account_id), acc.project_name);
        }
      });

      const projectNames: string[] = [];
      requests.forEach((req) => {
        let name = req.account_name;
        if (!name && req.account_id) {
          name = accountMap.get(String(req.account_id));
        }
        if (name && name.trim()) {
          const trimmed = name.trim();
          if (!projectNames.includes(trimmed)) {
            projectNames.push(trimmed);
          }
        }
      });

      const formattedProjectNames = projectNames
        .map(sanitizeSegment)
        .filter(Boolean);

      // Single Wing check
      const wingMap = new Map<string, string>();
      (this.wingList() || []).forEach((w) => {
        if (w.block_id && w.block_name) {
          wingMap.set(String(w.block_id), w.block_name);
        }
      });

      const wingNames: string[] = [];
      requests.forEach((req) => {
        let wName = req.block;
        if (!wName && (req as any).block_id) {
          wName = wingMap.get(String((req as any).block_id));
        }
        if (wName && wName.trim()) {
          const trimmed = wName.trim();
          if (!wingNames.includes(trimmed)) {
            wingNames.push(trimmed);
          }
        }
      });

      // Single Floor check
      const floorMap = new Map<string, string>();
      (this.floorList() || []).forEach((f) => {
        if (f.floor_id && f.floor_name) {
          floorMap.set(String(f.floor_id), f.floor_name);
        }
      });

      const floorNames: string[] = [];
      requests.forEach((req) => {
        let fName = (req as any).floor_name || (req as any).floor;
        if (!fName && (req as any).floor_id) {
          fName = floorMap.get(String((req as any).floor_id));
        }
        if (fName && String(fName).trim()) {
          const trimmed = String(fName).trim();
          if (!floorNames.includes(trimmed)) {
            floorNames.push(trimmed);
          }
        }
      });

      // Single Unit check
      const unitMap = new Map<string, string>();
      (this.unitList() || []).forEach((u) => {
        if (u.floor_unit_id && u.floor_unit) {
          unitMap.set(String(u.floor_unit_id), u.floor_unit);
        }
      });

      const unitNames: string[] = [];
      requests.forEach((req) => {
        let uName = req.floor_unit || (req as any).unit;
        if (!uName && (req as any).floor_unit_id) {
          uName = unitMap.get(String((req as any).floor_unit_id));
        }
        if (uName && uName.trim()) {
          const trimmed = uName.trim();
          if (!unitNames.includes(trimmed)) {
            unitNames.push(trimmed);
          }
        }
      });

      // Assemble filename parts
      const nameParts: string[] = [];
      if (formattedProjectNames.length > 0) {
        nameParts.push(formattedProjectNames.join('_'));
      } else {
        const tab = this.activeTab();
        const tabLabel =
          tab === 'all'
            ? 'all-requests'
            : tab === 'open'
            ? 'open-requests'
            : 'closed-requests';
        nameParts.push(tabLabel);
      }

      if (wingNames.length === 1) {
        const sWing = sanitizeSegment(wingNames[0]);
        if (sWing) {
          nameParts.push(sWing);
        }
      }

      if (floorNames.length === 1) {
        const sFloor = sanitizeSegment(floorNames[0]);
        if (sFloor) {
          nameParts.push(sFloor);
        }
      }

      if (unitNames.length === 1) {
        const sUnit = sanitizeSegment(unitNames[0]);
        if (sUnit) {
          nameParts.push(sUnit);
        }
      }

      const pdfFileName = `${nameParts.join('_')}.pdf`;

      await exportRequestsToPdf({
        requests,
        storageUrl: this.storageUrl,
        preparedBy,
        fileName: pdfFileName,
      });
      this.snackBar.open('PDF exported successfully', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('PDF export failed:', error);
      this.snackBarService.showError('Failed to export PDF. Please try again.');
    } finally {
      this.exportingPdf.set(false);
    }
  }

  private getImageUrl(issue: Issue): string {
    const photos = issue.issuePhotos && issue.issuePhotos.length > 0
      ? issue.issuePhotos
      : issue.issue_photo
        ? issue.issue_photo.split(',').filter(Boolean)
        : [];

    if (!photos.length) {
      return '';
    }

    const cleanStorageUrl = this.storageUrl ? this.storageUrl.replace(/\/$/, '') : '';
    return photos
      .map((p) => {
        const cleanPhoto = p.trim().replace(/^\//, '');
        return cleanStorageUrl ? `${cleanStorageUrl}/${cleanPhoto}` : cleanPhoto;
      })
      .join(', ');
  }

  exportTableToExcel(data: Issue[], fileName = 'requests.xlsx'): void {
    const exportData = (data || []).map((issue, index) => ({
      'Sl No.': index + 1,
      'Category': issue.category_name || '',
      'Sub Category': issue.sub_category_name || '',
      'Date': issue.created_at
        ? new Date(issue.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '',
      'Wing': issue.block || '',
      'Unit': issue.floor_unit || '',
      'Priority': issue.priority_name || '',
      'Status': issue.status_name || '',
      'Project': issue.account_name || '',
      'Raised By': issue.reported_name || '',
      'Assigned To': issue.assigned_to_name || '',
      'Description': issue.description || '',
      'Image': this.getImageUrl(issue),
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Requests');
    XLSX.writeFile(wb, fileName);
  }

  private formatPdfFileDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Apply filter button handler
  onApplyFilter(): void {
    if (!this.filterForm) {
      return;
    }

    const {
      account,
      status,
      start_date,
      end_date,
      category,
      sub_category,
      priority,
      wing,
      floor,
      unit_type,
      unit,
    } = this.filterForm.value;

    // Update signals used by computed filters
    this.selectedAccount.set(account ?? '');
    this.selectedStatus.set(status ?? '');
    this.selectedStartDate.set(start_date ?? '');
    this.selectedEndDate.set(end_date ?? '');
    this.selectedCategory.set(category ?? '');
    this.selectedSubCategory.set(sub_category ?? '');
    this.selectedPriority.set(priority ?? '');
    this.selectedWing.set(wing ?? '');
    this.selectedFloor.set(floor ?? '');
    this.selectedUnitType.set(unit_type ?? '');
    this.selectedUnit.set(unit ?? '');

    this.cardCurrentPage.set(1);
    this.fetchAllIssues();
  }

  // Optimized HTTP calls with caching and error handling
  private createHttpObservable<T>(
    observable: Observable<T>,
    errorMessage: string
  ): Observable<T> {
    return observable.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((error) => {
        console.error(errorMessage, error);
        this.handleError(error, errorMessage);
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    );
  }

  fetchAccounts(): void {
    const payload = { user_id: parseInt(this.userID || '0', 10) };
    const request$ = this.http.post<{ data: AccountOption[] }>(
      `${this.baseUrl}/assign_project_dropdown`,
      payload
    );

    this.createHttpObservable(request$, 'Failed to fetch accounts')
      .pipe(
        map((res) => res?.data || []),
        tap((accounts) => this.accountList.set(accounts))
      )
      .subscribe();
  }

  fetchAllPriority(): void {
    const request$ = this.http.get<PriorityOption[]>(
      `${this.baseUrl}/priority_dropdown`
    );

    this.createHttpObservable(request$, 'Failed to fetch priorities')
      .pipe(tap((priorities) => this.priorityList.set(priorities || [])))
      .subscribe();
  }

  fetchAllStatus(): void {
    const request$ = this.http.get<StatusOption[]>(
      `${this.baseUrl}/status_dropdown`
    );

    this.createHttpObservable(request$, 'Failed to fetch statuses')
      .pipe(tap((statuses) => this.statusList.set(statuses || [])))
      .subscribe();
  }

  allCategoryDropdown(): void {
    const accountGroupId = getAccountGroupIdFromSession();
    if (accountGroupId == null) {
      this.categoryList.set([]);
      return;
    }

    const payload = { account_group_id: accountGroupId };

    const request$ = this.raiseRequestService.getCategories(payload);

    this.createHttpObservable(request$, 'Failed to fetch categories')
      .pipe(
        map((res: any) => this.unwrapDropdownList(res)),
        tap((categories) => this.categoryList.set(categories || []))
      )
      .subscribe();
  }

  loadSubCategoryDropdown(categoryId?: string | number): void {
    const categoryIdToUse = categoryId || this.filterForm?.get('category')?.value;

    if (!categoryIdToUse) {
      this.subCategoryList.set([]);
      this.showSubCategoryFilter.set(false);
      return;
    }

    const request$ = this.raiseRequestService.getSubCategories({
      issue_category_id: categoryIdToUse,
    });

    this.createHttpObservable(request$, 'Failed to fetch sub categories')
      .pipe(
        map((res: any) => this.normalizeSubCategories(this.unwrapDropdownList(res))),
        tap((subCategories) => {
          this.subCategoryList.set(subCategories);
          this.showSubCategoryFilter.set(subCategories.length > 0);
          if (subCategories.length === 0) {
            this.filterForm.patchValue({ sub_category: '' }, { emitEvent: false });
          }
        })
      )
      .subscribe();
  }

  loadLocationFilterDropdowns(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();

    if (!accountIdToUse) {
      this.wingList.set([]);
      this.floorList.set([]);
      this.unitTypeList.set([]);
      this.unitList.set([]);
      return;
    }

    const wings$ = this.raiseRequestService.getBlockDropdown(accountIdToUse);
    const unitTypes$ = this.raiseRequestService.getUnitTypeDropdown(accountIdToUse);

    this.createHttpObservable(wings$, 'Failed to fetch wings')
      .pipe(
        map((res: any) => this.normalizeWings(this.unwrapDropdownList(res))),
        tap((wings) => this.wingList.set(wings))
      )
      .subscribe();

    this.createHttpObservable(unitTypes$, 'Failed to fetch unit types')
      .pipe(
        map((res: any) => this.normalizeUnitTypes(this.unwrapDropdownList(res))),
        tap((unitTypes) => this.unitTypeList.set(unitTypes))
      )
      .subscribe();

    this.refreshFloorDropdown(accountIdToUse);
    this.refreshUnitDropdown(accountIdToUse);
  }

  refreshFloorDropdown(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();
    const wingId = this.filterForm?.get('wing')?.value;

    if (!accountIdToUse || !wingId) {
      this.floorList.set([]);
      return;
    }

    const request$ = this.raiseRequestService.getFloorDropdown(
      accountIdToUse,
      wingId
    );

    this.createHttpObservable(request$, 'Failed to fetch floors')
      .pipe(
        map((res: any) => this.normalizeFloors(this.unwrapDropdownList(res))),
        tap((floors) => this.floorList.set(floors))
      )
      .subscribe();
  }

  refreshUnitDropdown(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();
    const wingId = this.filterForm?.get('wing')?.value;
    const floorId = this.filterForm?.get('floor')?.value;
    const unitType = this.filterForm?.get('unit_type')?.value;

    if (!accountIdToUse) {
      this.unitList.set([]);
      return;
    }

    const payload = {
      account_id: accountIdToUse,
      block_id: wingId || null,
      phase_id: null,
      floor_id: floorId || null,
      unit_type: unitType || null,
    };

    const request$ = this.raiseRequestService.getUnitDropdown(payload);

    this.createHttpObservable(request$, 'Failed to fetch units')
      .pipe(
        map((res: any) => this.normalizeUnits(this.unwrapDropdownList(res))),
        tap((units) => this.unitList.set(units))
      )
      .subscribe();
  }

  fetchAllIssues(): void {
    this.loading.set(true);
    const criteria = this.filterCriteria();
    const limit = this.cardItemsPerPage() || 50;
    const offset = (this.cardCurrentPage() - 1) * limit;

    let statusPayload: any = null;
    if (this.activeTab() === 'open') {
      statusPayload = [1, 2];
    } else if (this.activeTab() === 'closed') {
      statusPayload = [3];
    } else {
      statusPayload = this.toNullableFilterValue(criteria.status);
    }

    const payload: any = {
      account_id: parseInt(criteria.account || this.accountID || '0', 10),
      user_id: parseInt(this.userID || '0', 10),
      status: statusPayload,
      priority_id: this.toNullableFilterValue(criteria.priority),
      floor_unit_id: this.toNullableFilterValue(criteria.unit),
      internal_priority_id: null,
      issue_category_id: this.toNullableFilterValue(criteria.category),
      issue_sub_category_id: this.toNullableFilterValue(criteria.subCategory),
      block_id: this.toNullableFilterValue(criteria.wing),
      floor_id: this.toNullableFilterValue(criteria.floor),
      unit_type: criteria.unitType || null,
      limit: limit,
      offset: offset,
    };

    if (this.activeTab() === 'all') {
      payload.start_date = criteria.startDate || '2026-07-18';
      payload.end_date = criteria.endDate || '2026-08-21';
    }

    this.raiseRequestService
      .fetchAllIssues(payload)
      .pipe(
        map((res: any) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          const filtered = res?.filtered_count ?? res?.filteredCount ?? res?.count ?? (Array.isArray(res) ? res.length : 0);
          const total = res?.total_count ?? res?.totalCount ?? res?.total_records ?? res?.overall_total ?? filtered;
          this.filteredCount.set(filtered);
          this.totalCount.set(total);
          return (list || []).map((issue: Issue) => ({
            ...issue,
            issuePhotos: issue.issue_photo
              ? issue.issue_photo.split(',').filter(Boolean)
              : [],
          }));
        }),
        tap((issues) => {
          this.allRequestList.set(issues);
          this.startCarouselAutoSlide();
        }),
        catchError((error) => {
          console.error('Error fetching issues:', error);
          this.snackBarService.showError(
            'Failed to fetch issues, please try again later.'
          );
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  fetchUserProfile(): void {
    if (!this.userID) {
      console.warn('User ID not found in session storage');
      return;
    }

    this.apiService
      .fetchUserProfile(this.userID)
      .pipe(
        tap((profile: UserProfile) => this.userProfile.set(profile)),
        catchError((error) => {
          console.error('Error fetching user profile:', error);
          this.snackBarService.showError(
            'Failed to fetch user profile, please try again later.'
          );
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  // Carousel management with cleanup
  private startCarouselAutoSlide(): void {
    const timeoutId = this.carouselInitTimeout();
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = window.setTimeout(() => {
      this.initializeCarousels();
    }, 0);

    this.carouselInitTimeout.set(newTimeoutId);
  }

  private initializeCarousels(): void {
    const carouselElements = document.querySelectorAll<HTMLElement>(
      '.issue-card .carousel'
    );

    carouselElements.forEach((carouselEl) => {
      // Cleanup existing instance
      const existingInstance = bootstrap.Carousel.getInstance(carouselEl);
      if (existingInstance) {
        existingInstance.dispose();
        this.carouselInstances.delete(carouselEl);
      }

      // Create new instance
      try {
        const instance = new bootstrap.Carousel(carouselEl, {
          interval: 4000,
          ride: 'carousel',
          pause: false,
        });
        instance.cycle();
        this.carouselInstances.set(carouselEl, instance);
      } catch (error) {
        console.warn('Failed to initialize carousel:', error);
      }
    });
  }

  private cleanupCarousels(): void {
    this.carouselInstances.forEach((instance, element) => {
      try {
        instance.dispose();
      } catch (error) {
        console.warn('Error disposing carousel:', error);
      }
    });
    this.carouselInstances.clear();
  }

  // Dialog handlers with optimized refresh
  openAssignToDialog(item: Issue): void {
    const dialogRef = this.dialog.open(EditRequestComponent, {
      width: '706px',
      maxHeight: '90vh',
      data: { item, for: 'issue' },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchAllIssues();
        }
      });
  }

  openEditDialog(item: Issue): void {
    const dialogRef = this.dialog.open(EditRequestComponent, {
      width: '706px',
      maxHeight: '90vh',
      data: { item, for: 'issue' },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchAllIssues();
        }
      });
  }

  openComments(issueId: number): void {
    this.dialog.open(CommentComponent, {
      width: '560px',
      maxHeight: '90vh',
      data: { issueId },
    });
  }

  openAddIuusePhoto(issueId: number): void {
    const dialogRef = this.dialog.open(AdddPhotoComponent, {
      width: '560px',
      maxHeight: '90vh',
      data: { issueId },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchAllIssues();
        }
      });
  }

  openPreview(photoUrl: string): void {
    this.dialog.open(PreviewDialogComponent, {
      width: '65%',
      height: '88vh',
      data: {
        url: photoUrl,
        title: 'Image Preview',
        filename: photoUrl.split('/').pop() || 'image.jpg'
      }
    });
  }

  openUpdateStatus(issue: Issue): void {
    const dialogRef = this.dialog.open(UpdateSatatusComponent, {
      width: '500px',
      data: { item: issue },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchAllIssues();
        }
      });
  }

  // Image zoom handlers (if needed)
  zoom = signal(1);
  originX = signal(0);
  originY = signal(0);

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const step = 0.1;
    if (event.deltaY < 0) {
      this.zoom.update((z) => z + step);
    } else {
      this.zoom.update((z) => Math.max(1, z - step));
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (event.buttons === 1) {
      this.originX.update((x) => x + event.movementX);
      this.originY.update((y) => y + event.movementY);
    }
  }

  private isClosedRequest(issue: Issue): boolean {
    return String(issue.status) === String(this.CLOSED_STATUS_ID);
  }

  private toNullableFilterValue(value: string): number | null {
    if (!value) {
      return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private unwrapDropdownList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    return [];
  }

  private normalizeSubCategories(items: any[]): SubCategoryOption[] {
    return items.map((item) => ({
      issue_category_id: item.issue_category_id ?? item.issue_sub_category_id ?? item.sub_category_id ?? item.id,
      issue_category: item.issue_category ?? item.issue_sub_category ?? item.sub_category ?? item.name ?? '',
    }));
  }

  private normalizeWings(items: any[]): WingOption[] {
    return items.map((item) => ({
      block_id: item.block_id ?? item.wing_id ?? item.id,
      block_name: item.block_name ?? item.wing_name ?? item.name ?? '',
    }));
  }

  private normalizeFloors(items: any[]): FloorOption[] {
    return items.map((item) => ({
      floor_id: item.floor_id ?? item.id,
      floor_name: item.floor_name ?? item.name ?? '',
    }));
  }

  private normalizeUnitTypes(items: any[]): UnitTypeOption[] {
    return items.map((item) => ({
      unit_type: item.unit_type ?? item.unit_type_name ?? item.name ?? '',
    }));
  }

  private normalizeUnits(items: any[]): UnitOption[] {
    return items.map((item) => ({
      floor_unit_id: item.floor_unit_id ?? item.unit_id ?? item.id,
      floor_unit: item.floor_unit ?? item.unit_name ?? item.name ?? '',
      block_id: item.block_id,
      unit_type: item.unit_type,
    }));
  }

  // Error handling
  private handleError(error: unknown, message: string): void {
    console.error(message, error);
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  // TrackBy functions for performance
  trackByIssueId(_index: number, issue: Issue): number {
    return issue.issue_id;
  }

  trackByAccountId(_index: number, account: AccountOption): number | string {
    return account.account_id;
  }

  trackByStatusId(_index: number, status: StatusOption): number | string {
    return status.status_id;
  }

  trackByCategoryId(
    _index: number,
    category: CategoryOption
  ): number | string {
    return category.issue_category_id;
  }

  trackBySubCategoryId(
    _index: number,
    subCategory: SubCategoryOption
  ): number | string {
    return subCategory.issue_category_id;
  }

  trackByWingId(_index: number, wing: WingOption): number | string {
    return wing.block_id;
  }

  trackByFloorId(_index: number, floor: FloorOption): number | string {
    return floor.floor_id;
  }

  trackByUnitType(_index: number, unitType: UnitTypeOption): string {
    return unitType.unit_type;
  }

  trackByUnitId(_index: number, unit: UnitOption): number | string {
    return unit.floor_unit_id;
  }

  trackByPriorityId(
    _index: number,
    priority: PriorityOption
  ): number | string | undefined {
    return priority.priority_id ?? priority.id;
  }

  // Getters for template compatibility (keeping existing API)
  get allUserprofileList(): UserProfile | null {
    return this.userProfile();
  }
}